import React, { useState, useEffect } from "react";
import GYLogo from "../../../images/BASE64/GYLogo";
import { useFormatter, numToWordsBaht } from "../../../util/formatter";
import fonts_config from "./fonts_config.json";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import pdfMake from "pdfmake/build/pdfmake.js";

import moment from "moment-timezone";

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

const ReceiptTemplate_MSA = (props) => {
    const [formatNumber] = useFormatter();
    const [PDF, setPDF] = useState(null);
    const logo = GYLogo;

    const previewStyle = {
        width: "100%",
        height: "72vh",
    };

    /**
     * Generate Receipt PDF
     * @param {obj} dataObj
     */
    const generateReceipt = (dataObj) => {
        const invoiceData = !Array.isArray(dataObj.invoice)
            ? dataObj.invoice
            : dataObj.invoice[0];
        const receiptData = dataObj.receipt;
        const invoiceNo = receiptData.invoiceNo;
        const billingName =
            invoiceData.billing_name != null ? invoiceData.billing_name : "-";
        const billingAddress =
            invoiceData.billing_address != null ? invoiceData.billing_address : "-";
        const paymentDate =
            receiptData.paymentDate != null
                ? moment(receiptData.paymentDate).format("DD/MM/YYYY")
                : "-";
        
        const show_monthly_unitprice =  moment(invoiceData.dateData).diff(moment("2024-09-01", "YYYY-MM-DD")) >= 0;

        const customerID = !Array.isArray(dataObj.invoice)
            ? invoiceData.customer_id
            : invoiceData.company;
        const dateStr = invoiceNo.slice(-7, -3);
        const modifiedStr = "01-" + dateStr.slice(2, 4) + "-20" + dateStr.slice(0, 2);
        const selectedDate = moment(modifiedStr).format("DD/MM/YYYY");
        const _msaDate = moment(invoiceData.msa);
        const _startMonth = moment(selectedDate).startOf("month");
        const _endMonth = moment(selectedDate).endOf("month");
        const _msaStartMonth = _msaDate.diff(_startMonth, "years", true);
        const period = `${_msaStartMonth < 0 ? moment(_startMonth).format("DD/MM/YYYY") : moment(_msaDate).format("DD/MM/YYYY")} - ${moment(_endMonth).format("DD/MM/YYYY")}`;

        const additional = JSON.parse(invoiceData.additional_info);
        const msa_price = show_monthly_unitprice ? invoiceData.unit_price : additional?.msa_price.unit_price;
        const discount = invoiceData.special_discount;
        const monitPrice = msa_price * invoiceData.msa_day
        const subtotal = monitPrice + discount;
        const VAT = (invoiceData.vat).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0 });

        const taxAmount = invoiceData.vat * subtotal;
        const grandTotal = parseFloat(subtotal.toFixed(2)) + parseFloat(taxAmount.toFixed(2))

        let tableContents = {};

        tableContents = {
            monitPrice,
            subtotal,
            taxAmount,
            msa_price,
            grandTotal
        };

        const totalDueWords = numToWordsBaht(grandTotal.toFixed(2)).toUpperCase();
        const tableParam = {
            ...invoiceData,
            ...tableContents,
            dateStr,
            period,
            totalDueWords,
            VAT,
        };

        // Location section
        const entitySection = buildEntitySection();

        // Main table section
        const mainTableSection = buildMainTableSection(tableParam);
        const inWordsTableSection = buildInWordsTableSection(totalDueWords);

        // PDF config
        const docDefinition = {
            pageSize: "A4",
            content: [
                {
                    nodeName: "DIV",
                    columns: [
                        {
                            nodeName: "DIV",
                            width: "50%",
                            stack: [
                                {
                                    image: logo,
                                    width: 150,
                                },
                                ...entitySection,
                                {
                                    text: "TO",
                                    style: ["boldHeader"],
                                    margin: [0, 30, 0, 0],
                                },
                                {
                                    nodeName: "DIV",
                                    margin: [20, 7],
                                    stack: [
                                        {
                                            text: billingName,
                                            style: ["boldHeader"],
                                        },
                                        {
                                            text: billingAddress,
                                        },
                                        {
                                            text: `TAX ID : ${invoiceData.client_tax_id}`,
                                        }
                                    ],
                                },
                            ],
                        },
                        {
                            nodeName: "DIV",
                            width: "50%",
                            stack: [
                                // second column consists of paragraphs
                                { text: "Receipt / Tax Invoice", style: "header_lg" },
                                {
                                    margin: [35, 0, 0, 0],
                                    table: {
                                        body: [
                                            [
                                                {
                                                    text: "Receipt No.",
                                                    style: ["boldHeader"],
                                                },
                                                {
                                                    text: receiptData.receiptNo,
                                                    style: ["invoice_details"],
                                                },
                                            ],
                                            [
                                                {
                                                    text: "DATE",
                                                    style: ["boldHeader"],
                                                },
                                                {
                                                    text: paymentDate,
                                                    style: ["invoice_details"],
                                                },
                                            ],
                                            [
                                                {
                                                    text: "Ref. Invoice No.",
                                                    style: ["boldHeader"],
                                                },
                                                {
                                                    text: receiptData.invoiceNo,
                                                    style: ["invoice_details"],
                                                },
                                            ],                                            
                                        ],
                                    },
                                    layout: {
                                        defaultBorder: false,
                                    },
                                },
                            ],
                        },
                    ],
                },
                {
                    style: "table_main",
                    table: mainTableSection,
                    layout: {
                        defaultBorder: false,
                        paddingLeft: function (i, node) {
                            return 0;
                        },
                        paddingRight: function (i, node) {
                            return 0;
                        },
                        paddingTop: function (i, node) {
                            return 0;
                        },
                        paddingBottom: function (i, node) {
                            return 0;
                        },
                    },
                },
                {
                    table: inWordsTableSection,
                    layout: {
                        defaultBorder: false,
                        paddingLeft: function (i, node) {
                            return 0;
                        },
                        paddingRight: function (i, node) {
                            return 0;
                        },
                        paddingTop: function (i, node) {
                            return 0;
                        },
                        paddingBottom: function (i, node) {
                            return 0;
                        },
                    },
                },
            ],
            footer: [
                {
                    stack: [
                        {
                            text: [
                                "This is a computer-generated Receipt. No signature is required"
                            ],
                        },
                    ],
                    margin: [40, 20, 40, 0],
                    style: {
                        alignment: "center",
                    },
                },
            ],
            styles: {
                header_lg: {
                    fontSize: 27,
                    margin: [0, 0, 20, 15],
                    alignment: "right",
                },
                sub_header: {
                    decoration: "underline",
                },
                table_header: {
                    margin: [0, 1, 0, 0],
                    fontSize: 10,
                },
                table_main: {
                    margin: [0, 30, 0, 0],
                },
                boldHeader: { bold: true },
                savings: {
                    color: "green",
                    bold: true,
                },
                numInWords: {
                    fontSize: 7,
                },
                thai: {
                    font: "Trirong",
                    fontSize: 8,
                    margin: [0, 0, 0, -3],
                },
            },
            defaultStyle: {
                font: "Calibri",
                fontSize: 9,
            },
        };

        // Generate PDFMAKE document
        const pdfDocGenerator = pdfMake.createPdf(docDefinition);

        // Set PDFMAKE config for download in the later stage
        if (props.setPDFDoc != null) props.setPDFDoc(docDefinition);

        pdfDocGenerator.getDataUrl((dataUrl) => {
            setPDF(dataUrl);
        });
    };

    /**
     * Build entity section object
     * @param {Solar entity object} solarEntity
     * @returns Entity section object
     */
    const buildEntitySection = () => {
        const companyName = 'Green Yellow (Thailand) Co., LTD. (Head Office)'
        const taxID = '0105557184821';
        const accountantName = 'Khanatpat Kamnoi (Acct.)'
        const telephone = '+66 (0) 2-079-8081 Ext. 201'
        const address = '48/43-44, TISCO Tower, 19th Floor,_North Sathorn Road, Silom Sub-district,_Bangrak District, Bangkok 10500'
        const addressArr = address.split("_");
        const addressRows = addressArr.map((rows) => ({ text: rows }));

        const section = [
            {
                text: companyName,
                style: ["boldHeader"],
            },
            ...addressRows,
            {
                text: `TAX ID : ${taxID}`,
            },
            {
                text: `Contact person : ${accountantName}`,
            },
            {
                text: `Tel: ${telephone}`,
            },
        ];

        return section;
    };

    /**
     * Build main table section
     * @param {obj} data
     * @returns Main table section
     */
    const buildMainTableSection = (data) => {
        let bodyArr = [
            [
                {
                    text: "DESCRIPTION",
                    border: [false, true, false, true],
                    style: ["table_header", "boldHeader"],
                },
                {
                    border: [false, true, false, true],
                    text: "QUANTITY (days)",
                    alignment: "center",
                    style: ["table_header", "boldHeader"],
                },
                {
                    border: [false, true, false, true],
                    text: "Unit Price (THB)",
                    alignment: "right",
                    style: ["table_header", "boldHeader"],
                },
                {
                    border: [false, true, false, true],
                    text: "Total Amount (THB)",
                    alignment: "right",
                    style: ["table_header", "boldHeader"],
                },
            ],
        ];

        const fullRows = [
            [
                {
                    text: `\nPeriod: ${data.period}`,
                    style: ["table_main_rows", "boldHeader"],
                    colSpan: 4,
                }
            ],
            [
                {
                    text: "Monitoring Service",
                    style: "table_main_rows",
                    rowSpan: 2,
                },
                {
                    text: data.msa_day > 0 ? data.msa_day : "-",
                    alignment: "center",
                    rowSpan: 2,
                    style: "table_main_rows",
                    padding: 0,
                },
                {
                    text: data.msa_price > 0 ? formatNumber(data.msa_price, 2) : "-",
                    alignment: "right",
                    rowSpan: 2,
                    style: "table_main_rows",
                },
                {
                    text: data.monitPrice ? formatNumber(data.monitPrice, 2) : "-",
                    alignment: "right",
                    rowSpan: 2,
                    style: ["table_main_rows", "boldHeader"],
                },
            ],
            ["", "", "", ""],
            buildExtraLinesSection(data.special_discount_comment, data.special_discount)
        ];

        bodyArr = [...bodyArr, ...fullRows];

        return {
            body: [
                ...bodyArr,  ["", "", "", ""],
                [
                    {
                        colSpan: 3,
                        text: "\nSubtotal",
                        alignment: "right",
                        style: ["table_main_rows", "boldHeader"],
                    },
                    "",
                    "",
                    {
                        text: "\n" + formatNumber(data.subtotal, 2),
                        alignment: "right",
                        style: ["table_main_rows", "boldHeader"],
                    },
                ],
                [
                    {
                        colSpan: 3,
                        text: `VAT ${data.VAT}`,
                        alignment: "right",
                        style: ["table_main_rows", "boldHeader"],
                    },
                    "",
                    "",
                    {
                        text: formatNumber(data.taxAmount, 2),
                        alignment: "right",
                        style: ["table_main_rows", "boldHeader"],
                    },
                ],
                [
                    {
                        colSpan: 3,
                        text: "Grand Total",
                        alignment: "right",
                        border: [false, true, false, true],
                        style: "boldHeader",
                        margin: [0, 1, 0, 0],
                    },
                    { text: "", border: [false, true, false, true] },
                    { text: "", border: [false, true, false, true] },
                    {
                        text: formatNumber(data.grandTotal, 2),
                        alignment: "right",
                        border: [false, true, false, true],
                        style: "boldHeader",
                        margin: [0, 1, 0, 0],
                    },
                ],
            ],
            widths: ["55%", "15%", "15%", "15%"],
        };
    };

    /**
      * Build extraline section object
      * @param {Bank account obj} bankAccount
      * @returns Extraline section obj
      */
    const buildExtraLinesSection = (description, amount) => {
        if (amount === null) return ["", "", "", ""];
        const descriptions = description.split(" / ");
        const descriptionEN = descriptions[0];
        return [
            {
                text: [
                    {
                        text: `${descriptionEN} \n`,
                        italics: true,
                        style: { lineHeight: 1.6 }
                    },
                ],
                rowSpan: 2,
                style: "table_main_rows",
            }, "", "",
            {
                text: formatNumber(amount, 2),
                style: "table_main_rows",
                rowSpan: 2,
                bold: true,
                alignment: "right",
            }
        ]
    }

    /**
     * Build total in words table
     * @param {string} grandTotal
     * @returns Total in words section
     */
    const buildInWordsTableSection = (grandTotal) => {
        return {
            body: [
                [
                    {
                        text: "Total (In words)",
                        border: [false, false, true, true],
                        style: "boldHeader",
                        margin: [0, 1, 0, 0],
                    },
                    {
                        text: grandTotal,
                        style: "numInWords",
                        border: [false, false, false, true],
                        margin: [3, 1, 0, 0],
                    },
                ],
            ],
            widths: ["20%", "80%"],
        };
    };

    useEffect(() => {
        if (props.receipt != null) generateReceipt(props.receipt);

        return () => setPDF(null);
    }, [props.receipt]);

    return (
        <div>
            <iframe style={previewStyle} src={PDF} />
        </div>
    );
};

export default ReceiptTemplate_MSA;
